/* EditorLayoutRenderer.js */

"use strict";
const _ = require("lodash");
const GoldenLayoutCrawler = require("./GoldenLayoutCrawler");
const REGISTERED_EDITOR_NAME = "STEVE-Editor";

class EditorLayoutRenderer {
    constructor(model) {
        this._model = model;
        this._rootElem = document.createElement("editor-layout-gl");
        this._rootElem.component = this._model;
        this._rootElem.classList.add("absolute");
        this._goldenLayoutCrawler = new GoldenLayoutCrawler(this._rootElem);
        this._goldenLayoutCrawler.initialize();
        this._goldenLayoutCrawler.registerEditor(
            REGISTERED_EDITOR_NAME,
            this._model._createEditorFactory()
        );
        this._goldenLayoutCrawler.registerOnBeforeUnload(
            (this._onBeforeUnload = this._onBeforeUnload.bind(this))
        );

        this._registerListeners();
    }

    /********************************************/
    /* START PRIVATE COMPONENT RENDERER METHODS */
    /********************************************/
    _registerListeners() {
        this._model.on("ResizeContainer", (this._onResizeEvent = this._onResizeEvent.bind(this)));

        this._model.on("FocusEditor", (this._onFocusEditor = this._onFocusEditor.bind(this)));
        this._model.on(
            "ToggleFullscreen",
            (this._onToggleFullscreen = this._onToggleFullscreen.bind(this))
        );

        this._model.on("CloseEditor", (this._onCloseEditor = this._onCloseEditor.bind(this)));

        this._goldenLayoutCrawler.on(
            "EditorDestroyed",
            (this._onEditorItemDestroyed = this._onEditorItemDestroyed.bind(this))
        );
        this._goldenLayoutCrawler.on(
            "activeContentItemChanged",
            this._onActiveTabChanged.bind(this)
        );
    }
    _unregisterListeners() {
        this._model.removeListener("ResizeContainer", this._onResizeEvent);

        this._model.removeListener("FocusEditor", this._onFocusEditor);
        this._model.removeListener("ToggleFullscreen", this._onToggleFullscreen);
        this._model.removeListener("CloseEditor", this._onCloseEditor);

        this._goldenLayoutCrawler.removeListener("ItemDestroyed", this._onEditorItemDestroyed);
        this._goldenLayoutCrawler.removeListener(
            "activeContentItemChanged",
            this._onActiveTabChanged
        );
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    shutdown() {
        let shutdownProms = [];
        this._unregisterListeners();
        //Tell all the Editors they have been resized...
        let launchedEditors = this._model.getLaunchedEditors();
        launchedEditors.forEach((le) => {
            shutdownProms.push(le.shutdown());
        });
        shutdownProms.push(this._goldenLayoutCrawler.shutdown());
        this._model = undefined;
        this._rootElem.remove();
        this._rootElem = undefined;

        return Promise.all(shutdownProms);
    }

    /**
     * Stateless method to completely rebuild this component from the _rootElem down.
     */
    render() {
        //TODO Idempotent Rendering to be added
    }
    get rootElement() {
        return this.getRootElement();
    }
    getRootElement() {
        return this._rootElem;
    }
    get model() {
        return this.getModel();
    }
    getModel() {
        return this._model;
    }

    /*******************************************/
    /* END COMPONENT RENDERER METHODS */
    /* START HANDLER METHODS */
    /*******************************************/
    _onResizeEvent(sizes) {
        this._resizeContainer(sizes);
    }
    _onFocusEditor(editorId) {
        this._goldenLayoutCrawler.focusEditor(editorId);
    }
    _onToggleFullscreen(editorId) {
        this._goldenLayoutCrawler.toggleFullscreen(editorId);
    }
    _onCloseEditor(editorId) {
        let closeTheEditor = () => {
            this._goldenLayoutCrawler.closeEditor(editorId);
        };
        let resultProm = this._onBeforeUnload(editorId);
        Promise.resolve(resultProm)
            .then((result) => {
                if (!result) {
                    closeTheEditor();
                }
            })
            .catch(() => {
                //catch is an implied cancel.
            });
    }
    _onEditorItemDestroyed(itemId) {
        this._model.editorClosed(itemId);
    }

    _onActiveTabChanged(item) {
        this._model.activeTabChanged(item);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    /**
     * Called when editor's are closed, if pronise is resolved with a true it will prevent the close.
     * @return {Promise.<boolean>}
     * @private
     */
    _onBeforeUnload(editorId) {
        let editor = this._model.getLaunchedEditor(editorId);
        if (_.isFunction(editor.instance.onBeforeUnload)) {
            return editor.instance.onBeforeUnload(editorId);
        } else {
            return Promise.resolve(false);
        }
    }

    /**
     * Resize the entire Editor Layout due to container resize.
     * @param {Collection.<ClientRect>} sizes - A collection of ClientRect objects which inlcudes positioning top,left,right,bottom relative to the viewport.
     */
    _resizeContainer(/*sizes*/) {
        let clientRect = this._rootElem.parentElement.getBoundingClientRect();
        let width = clientRect.width;
        let height = clientRect.height;
        this._goldenLayoutCrawler.updateSize(width, height);

        //Tell all the Editors they have been resized...
        let launchedEditors = this._model.getLaunchedEditors();
        launchedEditors.forEach((le) => {
            if (_.isFunction(le.instance.resizeContainer)) {
                le.instance.resizeContainer(width, height);
            }
        });
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*  START EDITOR LAYOUT PUBLIC API METHODS */
    /*******************************************/
    launchEditor(editorDetails) {
        return this._goldenLayoutCrawler.launchEditor(REGISTERED_EDITOR_NAME, editorDetails);
    }
    getCurrentEditorLayoutState() {
        return this._goldenLayoutCrawler.getEditorItems();
    }
    reinitializeEditorLayoutState(state) {
        return this._goldenLayoutCrawler.reinitialize(state);
    }

    /*******************************************/
    /*  END EDITOR LAYOUT PUBLIC API METHODS */
    /*******************************************/
}
module.exports = EditorLayoutRenderer;
